import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  useBulkDeleteLines,
  useCancelTender,
  useCloseTender,
  useDuplicateTender,
  usePublishTender,
  useRemoveLine,
  useRemoveParticipant,
  useResendInvitation,
  useTenderDetail,
  useUnlinkContactList,
  useUpdateLine,
  useUpdateTenderMeta,
} from '@/api/sourcing/sourcing.hooks';
import { useContactLists } from '@/api/contact-list/contact-list.hooks';
import type { TenderDetail as TenderDetailType } from '@/api/sourcing/sourcing.types';
import { DsButton, DsInput, DsTabNav, DsTextarea } from '../../ds';
import type { DsTab } from '../../ds';
import {
  STATUS_LABEL,
  fmtDate,
  fmtDateTime,
  fromDateTimeLocal,
  statusPillClass,
  toDateTimeLocal,
} from '../helpers';
import { LinePicker } from './line-picker.component';
import { ParticipantPicker } from './participant-picker.component';
import { VerteilerPicker } from './verteiler-picker.component';
import { OfferMatrix } from './offer-matrix.component';

const TABS: DsTab[] = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'offers', label: 'Angebote' },
];

interface Props {
  tenderId: string;
}

export function TenderDetail({ tenderId }: Props) {
  const { data: tender, isPending } = useTenderDetail(tenderId);
  const [tab, setTab] = useState('overview');

  if (isPending || !tender) {
    return <div className="sc-empty">Lädt…</div>;
  }

  return (
    <div className="sc-detail">
      <DetailHeader tender={tender} />
      <DsTabNav
        tabs={TABS}
        activeTab={tab}
        onTabChange={setTab}
        maxWidth={1200}
      />
      <div className="sc-detail-body ds-scroll">
        <div className="sc-detail-body-inner">
          {tab === 'overview' ? (
            <Overview tender={tender} />
          ) : (
            <OfferMatrix tenderId={tenderId} status={tender.status} />
          )}
        </div>
      </div>
    </div>
  );
}

function DetailHeader({ tender }: { tender: TenderDetailType }) {
  const { enqueueSnackbar } = useSnackbar();
  const publish = usePublishTender();
  const close = useCloseTender();
  const cancel = useCancelTender();
  const duplicate = useDuplicateTender();
  const busy =
    publish.isPending ||
    close.isPending ||
    cancel.isPending ||
    duplicate.isPending;

  const doCancel = () => {
    if (!window.confirm(`Ausschreibung ${tender.reference} abbrechen?`)) return;
    cancel.mutate(tender.id, {
      onSuccess: () =>
        enqueueSnackbar('Ausschreibung abgebrochen.', { variant: 'info' }),
    });
  };

  return (
    <div className="sc-detail-head">
      <div className="sc-detail-head-inner">
      <div className="sc-detail-title-row">
        <span className="sc-detail-ref">{tender.reference}</span>
        <span className={`ds-pill ${statusPillClass(tender.status)}`}>
          {STATUS_LABEL[tender.status]}
        </span>
        <span className="sc-detail-actions">
          {tender.status === 'DRAFT' && (
            <DsButton
              variant="primary"
              size="small"
              disabled={busy}
              onClick={() =>
                publish.mutate(tender.id, {
                  onSuccess: () =>
                    enqueueSnackbar('Veröffentlicht — Lieferanten eingeladen.', {
                      variant: 'success',
                    }),
                })
              }
            >
              Veröffentlichen
            </DsButton>
          )}
          {tender.status === 'PUBLISHED' && (
            <DsButton
              variant="primary"
              size="small"
              disabled={busy}
              onClick={() =>
                close.mutate(tender.id, {
                  onSuccess: () =>
                    enqueueSnackbar('Ausschreibung geschlossen.', {
                      variant: 'success',
                    }),
                })
              }
            >
              Schließen
            </DsButton>
          )}
          {(tender.status === 'DRAFT' ||
            tender.status === 'PUBLISHED' ||
            tender.status === 'CLOSED') && (
            <DsButton size="small" disabled={busy} onClick={doCancel}>
              Abbrechen
            </DsButton>
          )}
          <DsButton
            size="small"
            disabled={busy}
            onClick={() =>
              duplicate.mutate(tender.id, {
                onSuccess: (clone) =>
                  enqueueSnackbar(`Kopie ${clone.reference} angelegt.`, {
                    variant: 'success',
                  }),
              })
            }
          >
            Duplizieren
          </DsButton>
        </span>
      </div>
      <h2 className="sc-detail-name">{tender.title}</h2>
      <div className="sc-detail-meta">
        <span>Frist: {fmtDateTime(tender.deadlineAt)}</span>
        {tender.publishedAt && (
          <span>Veröffentlicht: {fmtDate(tender.publishedAt)}</span>
        )}
        {tender.awardedAt && <span>Vergeben: {fmtDate(tender.awardedAt)}</span>}
      </div>
      </div>
    </div>
  );
}

function Overview({ tender }: { tender: TenderDetailType }) {
  const isDraft = tender.status === 'DRAFT';
  return (
    <>
      {isDraft && <MetaForm tender={tender} />}
      {!isDraft && tender.description && (
        <section className="sc-section">
          <div className="ds-pane-header">
            <h3>Beschreibung</h3>
          </div>
          <p className="sc-description">{tender.description}</p>
        </section>
      )}
      <LinesSection tender={tender} />
      <ParticipantsSection tender={tender} />
    </>
  );
}

function MetaForm({ tender }: { tender: TenderDetailType }) {
  const { enqueueSnackbar } = useSnackbar();
  const updateMeta = useUpdateTenderMeta();
  const [title, setTitle] = useState(tender.title);
  const [description, setDescription] = useState(tender.description ?? '');
  const [deadline, setDeadline] = useState(toDateTimeLocal(tender.deadlineAt));

  const save = () => {
    updateMeta.mutate(
      {
        id: tender.id,
        body: {
          title: title.trim(),
          description: description.trim(),
          deadlineAt: fromDateTimeLocal(deadline),
        },
      },
      {
        onSuccess: () =>
          enqueueSnackbar('Gespeichert.', { variant: 'success' }),
      },
    );
  };

  return (
    <section className="sc-section">
      <div className="ds-pane-header">
        <h3>Eckdaten</h3>
      </div>
      <div className="sc-form">
        <label className="sc-field">
          <span className="sc-field-label">Titel</span>
          <DsInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="sc-field">
          <span className="sc-field-label">Beschreibung (für Lieferanten)</span>
          <DsTextarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="sc-field">
          <span className="sc-field-label">Angebotsfrist</span>
          <input
            type="datetime-local"
            className="ds-input"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </label>
        <div>
          <DsButton
            variant="primary"
            size="small"
            disabled={updateMeta.isPending}
            onClick={save}
          >
            Speichern
          </DsButton>
        </div>
      </div>
    </section>
  );
}

function LinesSection({ tender }: { tender: TenderDetailType }) {
  const isDraft = tender.status === 'DRAFT';
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(
    new Set(),
  );
  const updateLine = useUpdateLine();
  const removeLine = useRemoveLine();
  const bulkDelete = useBulkDeleteLines();
  const { enqueueSnackbar } = useSnackbar();

  const toggleLine = (id: string) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedLineIds((prev) =>
      prev.size === tender.lines.length
        ? new Set()
        : new Set(tender.lines.map((l) => l.id)),
    );
  };
  const handleBulkDelete = () => {
    if (selectedLineIds.size === 0) return;
    if (
      !window.confirm(
        `${selectedLineIds.size} Position(en) wirklich entfernen?`,
      )
    )
      return;
    bulkDelete.mutate(
      { id: tender.id, lineIds: [...selectedLineIds] },
      {
        onSuccess: ({ deleted }) => {
          enqueueSnackbar(`${deleted} Position(en) entfernt.`, {
            variant: 'success',
          });
          setSelectedLineIds(new Set());
        },
      },
    );
  };

  return (
    <section className="sc-section">
      <div className="ds-pane-header">
        <h3>Artikel ({tender.lines.length})</h3>
        {isDraft && (
          <span className="sc-detail-actions">
            {selectedLineIds.size > 0 && (
              <DsButton
                size="small"
                disabled={bulkDelete.isPending}
                onClick={handleBulkDelete}
              >
                {selectedLineIds.size} entfernen
              </DsButton>
            )}
            <DsButton size="small" onClick={() => setPickerOpen(true)}>
              + Artikel
            </DsButton>
          </span>
        )}
      </div>
      {tender.lines.length === 0 ? (
        <p className="sc-hint">Noch keine Artikel hinzugefügt.</p>
      ) : (
        <table className="ds-table sc-inner-table">
          <thead>
            <tr>
              {isDraft && (
                <th className="sc-checkbox-col">
                  <input
                    type="checkbox"
                    checked={
                      selectedLineIds.size > 0 &&
                      selectedLineIds.size === tender.lines.length
                    }
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedLineIds.size > 0 &&
                          selectedLineIds.size < tender.lines.length;
                      }
                    }}
                    onChange={toggleAll}
                  />
                </th>
              )}
              <th>Artikelnr.</th>
              <th>Bezeichnung</th>
              <th>EAN</th>
              <th className="num">Menge</th>
              {isDraft && <th />}
            </tr>
          </thead>
          <tbody>
            {tender.lines.map((line) => (
              <tr key={line.id}>
                {isDraft && (
                  <td className="sc-checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedLineIds.has(line.id)}
                      onChange={() => toggleLine(line.id)}
                    />
                  </td>
                )}
                <td className="sc-mono">{line.itemId}</td>
                <td>{line.itemName}</td>
                <td className="sc-mono">{line.itemEan ?? '—'}</td>
                <td className="num">
                  {isDraft ? (
                    <input
                      type="number"
                      className="ds-input sc-qty-input"
                      defaultValue={line.quantity}
                      min={1}
                      onBlur={(e) => {
                        const q = parseInt(e.target.value, 10);
                        if (q > 0 && q !== line.quantity) {
                          updateLine.mutate({
                            id: tender.id,
                            lineId: line.id,
                            body: { quantity: q },
                          });
                        }
                      }}
                    />
                  ) : (
                    line.quantity
                  )}
                </td>
                {isDraft && (
                  <td className="sc-row-action">
                    <button
                      className="sc-link-btn"
                      onClick={() =>
                        removeLine.mutate({
                          id: tender.id,
                          lineId: line.id,
                        })
                      }
                    >
                      Entfernen
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {pickerOpen && (
        <LinePicker
          tenderId={tender.id}
          existingItemIds={tender.lines.map((l) => l.itemId)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </section>
  );
}

function ParticipantsSection({ tender }: { tender: TenderDetailType }) {
  const isDraft = tender.status === 'DRAFT';
  const isPublished = tender.status === 'PUBLISHED';
  const [pickerOpen, setPickerOpen] = useState(false);
  const [verteilerPickerOpen, setVerteilerPickerOpen] = useState(false);
  const removeParticipant = useRemoveParticipant();
  const unlinkContactList = useUnlinkContactList();
  const resend = useResendInvitation();
  const { data: allLists = [] } = useContactLists();
  const { enqueueSnackbar } = useSnackbar();

  const linkedLists = allLists.filter((l) =>
    tender.contactListIds.includes(l.id),
  );

  const hasActionCol = isDraft || isPublished;
  const withMessages = tender.participants.filter((p) => p.messageFromSupplier);

  return (
    <section className="sc-section">
      <div className="ds-pane-header">
        <h3>Lieferanten ({tender.participants.length})</h3>
        {isDraft && (
          <>
            <DsButton
              size="small"
              onClick={() => setVerteilerPickerOpen(true)}
            >
              + Verteiler
            </DsButton>
            <DsButton size="small" onClick={() => setPickerOpen(true)}>
              + Lieferant
            </DsButton>
          </>
        )}
      </div>
      {(linkedLists.length > 0 || tender.contactListIds.length > 0) && (
        <div className="sc-vt-bar">
          <span className="sc-vt-bar-label">Verteiler:</span>
          {linkedLists.length === 0 ? (
            // Tender has Verteiler IDs but the lists aren't visible to the
            // current user (different owner). Render placeholder chips.
            tender.contactListIds.map((id) => (
              <span key={id} className="sc-vt-chip muted">
                Verteiler #{id}
              </span>
            ))
          ) : (
            linkedLists.map((list) => (
              <span key={list.id} className="sc-vt-chip">
                {list.name}
                <span className="sc-vt-chip-count">
                  · {list.members.length}
                </span>
                {isDraft && (
                  <button
                    type="button"
                    className="sc-vt-chip-x"
                    aria-label="Entfernen"
                    disabled={unlinkContactList.isPending}
                    onClick={() =>
                      unlinkContactList.mutate(
                        { id: tender.id, listId: list.id },
                        {
                          onSuccess: () =>
                            enqueueSnackbar(
                              `Verteiler "${list.name}" entfernt.`,
                              { variant: 'success' },
                            ),
                        },
                      )
                    }
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          )}
        </div>
      )}
      {tender.participants.length === 0 ? (
        <p className="sc-hint">Noch keine Lieferanten eingeladen.</p>
      ) : (
        <table className="ds-table sc-inner-table">
          <thead>
            <tr>
              <th>Lieferant</th>
              <th>Ansprechpartner</th>
              <th>E-Mail</th>
              <th>Status</th>
              {hasActionCol && <th />}
            </tr>
          </thead>
          <tbody>
            {tender.participants.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.supplierName}
                  {!p.addedManually && (
                    <span
                      className="ds-pill muted sc-vt-tag"
                      title="Über Verteiler hinzugefügt"
                    >
                      Verteiler
                    </span>
                  )}
                </td>
                <td>{p.contactName ?? '—'}</td>
                <td className="sc-mono">{p.contactEmail}</td>
                <td>
                  {p.hasResponded ? (
                    <span className="ds-pill pos">Angebot abgegeben</span>
                  ) : p.invitedAt ? (
                    <>
                      <span className="ds-pill muted">Eingeladen</span>
                      {p.reminderCount > 1 && (
                        <span className="ds-pill warn">
                          {p.reminderCount - 1}× erinnert
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="ds-pill muted">Entwurf</span>
                  )}
                </td>
                {hasActionCol && (
                  <td className="sc-row-action">
                    {isDraft && (
                      <button
                        className="sc-link-btn"
                        onClick={() =>
                          removeParticipant.mutate({
                            id: tender.id,
                            participantId: p.id,
                          })
                        }
                      >
                        Entfernen
                      </button>
                    )}
                    {isPublished && !p.hasResponded && (
                      <button
                        className="sc-link-btn"
                        disabled={resend.isPending}
                        onClick={() =>
                          resend.mutate(
                            { id: tender.id, participantId: p.id },
                            {
                              onSuccess: () =>
                                enqueueSnackbar(
                                  `Einladung erneut gesendet an ${p.contactEmail}.`,
                                  { variant: 'success' },
                                ),
                            },
                          )
                        }
                      >
                        Erneut senden
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {withMessages.length > 0 && (
        <div className="sc-supplier-messages">
          <div className="ds-pane-header">
            <h4>Nachrichten von Lieferanten</h4>
          </div>
          {withMessages.map((p) => (
            <div className="sc-supplier-message" key={p.id}>
              <div className="sc-supplier-message-from">
                {p.supplierName}
                {p.contactName ? ` · ${p.contactName}` : ''}
              </div>
              <div className="sc-supplier-message-body">
                {p.messageFromSupplier}
              </div>
            </div>
          ))}
        </div>
      )}
      {pickerOpen && (
        <ParticipantPicker
          tenderId={tender.id}
          existingSupplierIds={tender.participants.map((p) => p.supplierId)}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {verteilerPickerOpen && (
        <VerteilerPicker
          tenderId={tender.id}
          linkedIds={tender.contactListIds}
          onClose={() => setVerteilerPickerOpen(false)}
        />
      )}
    </section>
  );
}
